import React, { forwardRef } from "react";
import { ChevronRightIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import VqqQuestionRenderer from "./VqqQuestionRenderer";
import { extractSectionCode } from "./sectionCode";

const RAG_CLASS = {
  Green: "bg-green-100 text-green-800 border-green-200",
  Yellow: "bg-yellow-100 text-yellow-800 border-yellow-200",
  Red: "bg-red-100 text-red-800 border-red-200",
};

const CRITICALITY_CLASS = {
  Critical: "bg-red-100 text-red-800 border-red-200",
  Major: "bg-orange-100 text-orange-800 border-orange-200",
  Minor: "bg-gray-100 text-gray-700 border-gray-200",
};

/**
 * 14030 — Section accordion for the Questionnaire & Risk Analysis tab.
 *
 * Header band layout:
 *   [▾] [TF1] [Major] Governance & Accountability (TF1)   ●10 [Green 100.0%] 10/10 10/10
 *
 * - Criticality tag is the dominant tier across the section's questions
 *   (max I*L*D), aligned with the per-question criticality the user sees
 *   inside the cards.
 * - The three count badges on the right mirror the sidebar: total ·
 *   scored/total · compliant/total.
 *
 * Falls back to the legacy "approved/total" badge when no aggregate is
 * provided (non-entitled viewers / unscored VQQs).
 */
const VqqSectionAccordion = forwardRef((
  {
    section,
    isOpen,
    onToggle,
    scoredRowsByQuestionId = null,
    methodology = null,
    aggregate = null,
    isAdmin = false,
    mode = null,
    missingGapTextIds = null,
    onSmeSave = null,
  },
  ref,
) => {
  if (!section) return null;

  const code = extractSectionCode(section.section);
  const total = section.question_count ?? aggregate?.total ?? 0;
  const approved = section.approved_count ?? 0;
  const Chevron = isOpen ? ChevronDownIcon : ChevronRightIcon;

  const rag = aggregate?.rag;
  const scoreText =
    aggregate?.score != null ? `${rag} ${aggregate.score.toFixed(1)}%` : null;
  const criticality = aggregate?.criticality;
  // 14030 / 14033 AC #13 — rubric-derived intrinsic Section Risk Score badge.
  // Different scale (1-125 number) and different concept (intrinsic to the
  // template, NOT vendor-driven) from the vendor-driven Risk Score % above.
  // The two are surfaced together so reviewers can see both axes at a glance.
  const intrinsicScore = aggregate?.intrinsic_section_risk_score;
  const intrinsicRag = aggregate?.intrinsic_section_risk_rag;
  const intrinsicText =
    Number.isFinite(intrinsicScore)
      ? `${intrinsicRag || "Green"} ${Number(intrinsicScore).toFixed(1)}`
      : null;

  return (
    <section ref={ref} aria-labelledby={`section-${code}-heading`} className="scroll-mt-24">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-3 text-left hover:bg-amber-50 transition"
        aria-expanded={isOpen}
      >
        <Chevron className="h-4 w-4 text-amber-700 shrink-0" aria-hidden="true" />
        <span className="inline-flex items-center justify-center min-w-[2.25rem] h-7 px-1.5 rounded text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200 shrink-0">
          {code}
        </span>
        {criticality ? (
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
              CRITICALITY_CLASS[criticality] || CRITICALITY_CLASS.Minor
            }`}
          >
            {criticality}
          </span>
        ) : null}
        <h3
          id={`section-${code}-heading`}
          className="flex-1 text-sm font-semibold text-gray-900 truncate"
        >
          {section.section}
        </h3>

        <span className="inline-flex items-center gap-1 text-[11px] text-gray-600">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
          <span className="tabular-nums font-semibold">{aggregate?.total ?? total}</span>
        </span>
        {intrinsicText ? (
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${
              RAG_CLASS[intrinsicRag] || RAG_CLASS.Green
            }`}
            title="Section Risk Score — rubric-derived intrinsic risk of this section: Mean(QRS) + 0.5 × sample StDev. Higher = more intrinsic risk. Red ≥ 50 · Yellow 20–49 · Green < 20."
          >
            {intrinsicText}
          </span>
        ) : null}
        {scoreText ? (
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${
              RAG_CLASS[rag] || RAG_CLASS.Green
            }`}
            title="Risk Score % — vendor-driven aggregate: 1 − (Σ realized risk / Σ total intrinsic risk). Higher % is better. Green ≥ 80% · Yellow 50–79% · Red < 50%."
          >
            {scoreText}
          </span>
        ) : null}
        <span className="text-[11px] font-semibold text-emerald-700 tabular-nums" title="Scored / Total">
          {aggregate ? `${aggregate.scored}/${aggregate.total}` : `${approved}/${total}`}
        </span>
        {aggregate ? (
          <span className="text-[11px] font-semibold text-blue-700 tabular-nums" title="Compliant / Total">
            {aggregate.compliant}/{aggregate.total}
          </span>
        ) : null}
      </button>

      {isOpen ? (
        <ol className="space-y-3 mt-3">
          {(section.questions ?? []).map((q, idx) => (
            <li key={q.rfi_question_id}>
              <VqqQuestionRenderer
                question={q}
                index={idx + 1}
                scoredRow={scoredRowsByQuestionId?.get(q.rfi_question_id)}
                methodology={methodology}
                isAdmin={isAdmin}
                mode={mode}
                missingGapTextIds={missingGapTextIds}
                onSmeSave={onSmeSave}
              />
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  );
});

VqqSectionAccordion.displayName = "VqqSectionAccordion";

export default VqqSectionAccordion;
